import { type HttpClient, type HttpClientAjaxParams } from "silver";

export interface DictionaryParser<I> {
	parse(input: I): DictionaryResults;
}

export type DictionaryResults = {
	suggestions: string[];
	entries: DictionaryEntry[];
};

export type DictionaryText = {
	text: string;
	language_code: string;
};

export type DictionaryEntry = {
	headword: string;
	partOfSpeech?: string;
	definitions: DictionaryText[];
	pronunciation?: string;
	wavFile?: string;
	example?: string[];
	alt: string[];
	boldForms?: string[];
	label?: string;
};

export interface DictionarySourceInterface {
	search(word: string): Promise<DictionaryResults>;
}

export class DictionarySource<I> implements DictionarySourceInterface{

	private lastWord: string = "";
	private lastPromise?: Promise<DictionaryResults>;
	private abortController = new AbortController();

	constructor(
		private httpClient: HttpClient,
		private dictionaryUrlFn: (word: string) => string,
		private parser: DictionaryParser<I>,
		private params?: HttpClientAjaxParams
	) { }

	search(word: string): Promise<DictionaryResults> {
		if (word === this.lastWord && this.lastPromise) {
			return this.lastPromise;
		}
		return this.lastPromise = this.fetch(word);
	}

	private async fetch(word: string): Promise<DictionaryResults> {
		this.abortController.abort();
		this.abortController = new AbortController();
		const url = this.dictionaryUrlFn(word);
		const response: I = await this.httpClient.get(url, {
			...this.params,
			abortController: this.abortController
		});
		return this.parser.parse(response);
	}
}
